import { useCallback, useMemo, useRef } from 'react'
import type { BotProtectionRuntimeEventLogger } from './runtimeEventLogger'

export type BotProtectionRuntimeState =
  | 'idle'
  | 'loading_script'
  | 'script_ready'
  | 'rendering_widget'
  | 'widget_ready'
  | 'verifying'
  | 'healthy'
  | 'degraded'
  | 'failed'

export type BotProtectionRuntimeStateController = {
  getRuntimeState: () => BotProtectionRuntimeState
  setRuntimeState: (nextState: BotProtectionRuntimeState, context?: Record<string, unknown>) => void
}

export const useBotProtectionRuntimeState = (
  logRuntimeEvent: BotProtectionRuntimeEventLogger,
): BotProtectionRuntimeStateController => {
  const runtimeStateRef = useRef<BotProtectionRuntimeState>('idle')

  const getRuntimeState = useCallback(() => runtimeStateRef.current, [])

  const setRuntimeState = useCallback(
    (nextState: BotProtectionRuntimeState, context?: Record<string, unknown>) => {
      if (runtimeStateRef.current === nextState) return
      runtimeStateRef.current = nextState
      logRuntimeEvent('info', 'bot_protection.runtime_state_changed', {
        state: nextState,
        ...(context ?? {}),
      })
    },
    [logRuntimeEvent],
  )

  return useMemo(
    () => ({ getRuntimeState, setRuntimeState }),
    [getRuntimeState, setRuntimeState],
  )
}
